import { gameBridge, type AudioCue } from '../events/GameBridge'
import { gameSettings } from '../settings/GameSettings'

const MUTE_KEY = 'block-defense.audio-muted.v1'

class AudioSystem {
  private context: AudioContext | null = null
  private muted = this.loadMuted()
  private cleanups: Array<() => void> = []
  private lastPlayed = new Map<AudioCue, number>()

  get isMuted(): boolean { return this.muted }
  get volume(): number { return gameSettings.snapshot.volume }

  start(): () => void {
    this.cleanups.push(gameBridge.on('audioCue', ({ cue }) => this.play(cue)))
    return () => {
      this.cleanups.forEach((cleanup) => cleanup())
      this.cleanups = []
      void this.context?.close()
      this.context = null
    }
  }

  unlock(): void {
    if (this.muted) return
    this.context ??= new AudioContext()
    if (this.context.state === 'suspended') void this.context.resume()
  }

  toggleMuted(): boolean {
    this.muted = !this.muted
    try { window.localStorage.setItem(MUTE_KEY, String(this.muted)) } catch { /* storage can be unavailable */ }
    if (!this.muted) this.unlock()
    return this.muted
  }

  setVolume(volume: number): number {
    return gameSettings.update({ volume }).volume
  }

  private play(cue: AudioCue): void {
    if (this.muted) return
    this.unlock()
    if (!this.context) return
    const nowMs = performance.now()
    const throttle = cue.startsWith('SHOT_') ? 70 : cue === 'IMPACT' ? 45 : 0
    if (nowMs - (this.lastPlayed.get(cue) ?? 0) < throttle) return
    this.lastPlayed.set(cue, nowMs)

    if (cue === 'SHOT_SOL') this.tone(420, 760, 0.045, 'square', 0.022)
    else if (cue === 'SHOT_ETH') this.tone(220, 520, 0.11, 'sine', 0.035)
    else if (cue === 'SHOT_BTC') this.tone(105, 48, 0.19, 'sawtooth', 0.055)
    else if (cue === 'IMPACT') this.noise(0.045, 0.018)
    else if (cue === 'DESTROY') { this.tone(180, 70, 0.16, 'square', 0.035); this.noise(0.09, 0.025) }
    else if (cue === 'BUILD') this.chord([330, 494, 659], 0.18, 0.025)
    else if (cue === 'UPGRADE') this.chord([392, 523, 784], 0.26, 0.03)
    else if (cue === 'WAVE') this.chord([110, 165, 220], 0.38, 0.035)
    else if (cue === 'BREACH') { this.tone(92, 36, 0.34, 'sawtooth', 0.065); this.noise(0.2, 0.04) }
    else if (cue === 'PROTOCOL') this.chord([262, 392, 523], 0.34, 0.03)
    else if (cue === 'VICTORY') this.chord([262, 330, 392, 523], 0.7, 0.04)
    else if (cue === 'DEFEAT') this.chord([196, 147, 98], 0.75, 0.045)
  }

  private tone(from: number, to: number, duration: number, type: OscillatorType, volume: number): void {
    if (!this.context) return
    const start = this.context.currentTime
    const oscillator = this.context.createOscillator()
    const gain = this.context.createGain()
    oscillator.type = type
    oscillator.frequency.setValueAtTime(from, start)
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(20, to), start + duration)
    gain.gain.setValueAtTime(volume * this.volume, start)
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration)
    oscillator.connect(gain).connect(this.context.destination)
    oscillator.start(start)
    oscillator.stop(start + duration)
  }

  private chord(frequencies: number[], duration: number, volume: number): void {
    frequencies.forEach((frequency, index) => window.setTimeout(() => this.tone(frequency, frequency * 1.02, duration, 'sine', volume), index * 34))
  }

  private noise(duration: number, volume: number): void {
    if (!this.context) return
    const frames = Math.max(1, Math.floor(this.context.sampleRate * duration))
    const buffer = this.context.createBuffer(1, frames, this.context.sampleRate)
    const data = buffer.getChannelData(0)
    for (let index = 0; index < frames; index += 1) data[index] = Math.random() * 2 - 1
    const source = this.context.createBufferSource()
    const gain = this.context.createGain()
    const start = this.context.currentTime
    source.buffer = buffer
    gain.gain.setValueAtTime(volume * this.volume, start)
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration)
    source.connect(gain).connect(this.context.destination)
    source.start(start)
  }

  private loadMuted(): boolean {
    try { return window.localStorage.getItem(MUTE_KEY) === 'true' } catch { return false }
  }
}

export const audioSystem = new AudioSystem()
